import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind classes intelligently, handling conflicts.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format an ISO timestamp for display in driver UI.
 */
export function formatTimestamp(iso: string | Date | null | undefined): string {
  if (!iso) return '--';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '--';
  return d.toLocaleString();
}

/**
 * Generate an opaque client-side id (used for log/media queue rows).
 */
export function clientId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Sleep for a given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Throttle a function so the underlying implementation runs at most once
 * per interval, but always invokes a trailing call to ensure the
 * latest payload is captured.
 */
export function throttle<T extends (...args: unknown[]) => void>(
  fn: T,
  intervalMs: number,
): T {
  let lastInvoke = 0;
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;

  const wrapper = (...args: Parameters<T>) => {
    const now = Date.now();
    const elapsed = now - lastInvoke;
    lastArgs = args;

    if (elapsed >= intervalMs) {
      lastInvoke = now;
      fn(...args);
      lastArgs = null;
    } else if (!trailingTimer) {
      trailingTimer = setTimeout(() => {
        lastInvoke = Date.now();
        trailingTimer = null;
        if (lastArgs) {
          fn(...lastArgs);
          lastArgs = null;
        }
      }, intervalMs - elapsed);
    }
  };

  return wrapper as T;
}
